import {
  observable,
  action,
  configure,
  makeObservable,
  computed,
  runInAction
} from 'mobx';
import API from '@api';
import APIold from '@api/graphqlOld';
import APIStage from '@api/graphqlStageOld';
import { queryClient } from '@root/src/providers/query';
import { peopleBillAmountQueryOptions } from '@queries/billLog';
import { notCheckedNotificationsAmountQueryOptions } from '@queries/notifications';
import { lastDeliveredOrderIdQueryOptions } from '@queries/peopleOrders';
import { peopleProfileQueryOptions } from '@queries/peopleProfile';
import XStore from './XStore';
import CartStore from './CartStore';
import { v1 } from 'uuid';
import { IEdetector } from '@utils/browserUTLs';
import { isNewCart } from '@utils/cart';
import { isOtherRegion } from '@utils/regions';
import { MOSCOW_DEFAULT_STORE_ID } from '@constants/regions';
import type { PeopleSettingsModel } from '@schema/cart';
import type {
  AddPeopleAddressMutation,
  PeopleAddressInput,
  UpdatePeopleAddressMutation,
  MeQuery
} from '@schema/main';
import { cartToken } from '@store/CartStore';
import type {
  catalogSortingT,
  settingsT,
  storeAddressesT,
  storeUserT,
  addressesLastPickupT
} from './UserStore.type';
configure({
  enforceActions: 'observed',
  useProxies: IEdetector() ? 'never' : 'always'
});

const EPP_CATALOG_DEFAULT = 20;

const defaultUser: storeUserT = {
  id: -69,
  newLoyaltyProgram: null,
  hash: null,
  birthdayDate: null,
  mainImage: null,
  fio: null,
  phone: null,
  email: null,
  cookieId: null,
  bill: null,
  addresses: null,
  headerToken: 'test'
};

const defaultSettings: PeopleSettingsModel = {
  epp_catalog: EPP_CATALOG_DEFAULT,
  catalog_sorting: 'popular'
  // для различной базовой сортировки не залогиненого юзера
};

function __deleteCookie(name: string, path?: string, domain?: string): void {
  if (document.cookie.includes(`${name}=`)) {
    document.cookie = `${name}=${path ? `;path=${path}` : ''}${
      domain ? `;domain=${domain}` : ''
    };expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  }
}

function changeSortName(catalogSorting: any): catalogSortingT {
  const sort =
    catalogSorting === 'date-desc'
      ? 'newest'
      : catalogSorting === 'price-asc'
      ? 'priceUp'
      : catalogSorting === 'price-desc'
      ? 'priceDown'
      : catalogSorting === 'title'
      ? 'alphabet'
      : catalogSorting === 'discount'
      ? 'discount'
      : 'popular';

  return sort;
}

class Store {
  user: storeUserT = defaultUser;

  settings: PeopleSettingsModel & settingsT = defaultSettings;

  addressesLastPickup: addressesLastPickupT = [];

  loyaltyStatusUser: number | null = null;

  isLoad: boolean = false;

  isSubscribedToSms: boolean | null = null;

  isSubscribedToEmail: boolean | null = null;

  isSubscribedToPromo: boolean | null = null;

  lastDeliveredOrderId: number | null | undefined = null;

  notCheckedNotificationsAmount: number | null = null;

  isUserLoading = true;

  ordersThatUserCanChange: number[] = [];

  constructor() {
    makeObservable(this, {
      user: observable,
      addressesLastPickup: observable,
      settings: observable,
      isLoad: observable,
      loyaltyStatusUser: observable,
      isSubscribedToSms: observable,
      isSubscribedToPromo: observable,
      isSubscribedToEmail: observable,
      lastDeliveredOrderId: observable,
      ordersThatUserCanChange: observable,

      getNotCheckedNotificationsAmount: action,
      setNotCheckedNotificationsAmount: action,
      notCheckedNotificationsAmount: observable,

      presortedAddresses: computed,
      moscowAddresses: computed,

      isNewUser: computed,
      isUserLoggedIn: computed,
      isUserLoading: observable,
      setIsUserLoading: action,

      setLastDeliveredOrderId: action,
      getLastDeliveredOrderId: action,
      setLoyaltyStatusUser: action,
      getLoyaltyStatusUser: action,
      getFullUserInfo: action,
      removeAddress: action,
      addPeopleAddress: action,
      updatePeopleAddress: action,
      getUser: action,
      setUser: action,
      setSettings: action,
      updateIsload: action,
      logout: action,
      getIsSubscribedToSms: action,
      setIsIsSubscribedToSms: action,
      setIsSubscribedToEmail: action,
      getOrdersThatUserCanChange: action,
      setIsSubscribedToPromo: action
    });
  }

  get presortedAddresses(): storeAddressesT {
    return (
      this.user?.addresses
        ?.slice()
        ?.sort((a: any, b: any) => +b.primary - +a.primary) || []
    );
  }

  get moscowAddresses(): storeAddressesT {
    return this.presortedAddresses.filter(
      item => !isOtherRegion(item.region_city_id)
    );
  }

  get isUserLoggedIn(): boolean {
    return (!!this.user?.email || !!this.user?.phone) && !this.isUserLoading;
  }

  get isNewUser(): boolean {
    return !this.lastDeliveredOrderId;
  }

  getOrdersThatUserCanChange = async (): Promise<void> => {
    API.getOrdersThatUserCanChange()
      .then(res => {
        runInAction(() => {
          this.setOrdersThatUserCanChange(res.getOrdersThatUserCanChange || []);
        });
      })
      .catch(console.log);
  };

  setOrdersThatUserCanChange = (value: number[]): void => {
    this.ordersThatUserCanChange = value;
  };

  setLastDeliveredOrderId = (id: number | null | undefined): void => {
    this.lastDeliveredOrderId = id;
  };

  getLastDeliveredOrderId = async (): Promise<void> => {
    await queryClient
      .fetchQuery(lastDeliveredOrderIdQueryOptions())
      .then(res => {
        this.setLastDeliveredOrderId(res.getPeopleLastDeliveredOrderId);
      })
      .catch(err => {
        console.log('Error: ', err);
      });
  };

  getUser = async (): Promise<boolean> =>
    await API.authInfo()
      .then(res => {
        this.setSettings(res?.me?.settingsItem || defaultSettings);
        this.getFullUserInfo(
          res?.me?.cookie_id || undefined,
          res.me?.cartCookieId || cartToken
        ).catch(console.log);
        if (res?.me?.id) {
          this.getLoyaltyStatusUser();
          this.getIsSubscribedToSms();
          this.getLastDeliveredOrderId();
          this.getNotCheckedNotificationsAmount();
          this.getOrdersThatUserCanChange();
        }
        return true;
      })
      .catch(() => {
        this.getFullUserInfo('', cartToken);
        this.setIsUserLoading(false);
        return false;
      });

  getNotCheckedNotificationsAmount = async (): Promise<void> => {
    await queryClient
      .fetchQuery(notCheckedNotificationsAmountQueryOptions())
      .then(res => {
        this.setNotCheckedNotificationsAmount(
          res.notCheckedNotificationsAmount
        );
      });
  };

  setNotCheckedNotificationsAmount = (value: number): void => {
    this.notCheckedNotificationsAmount = value;
  };

  setIsIsSubscribedToSms = (value: boolean): void => {
    this.isSubscribedToSms = value;
  };

  setIsSubscribedToEmail = (value: boolean): void => {
    this.isSubscribedToEmail = value;
  };

  setIsSubscribedToPromo = (value: boolean): void => {
    this.isSubscribedToPromo = value;
  };

  getIsSubscribedToSms = async (): Promise<void> => {
    queryClient
      .fetchQuery(peopleProfileQueryOptions())
      .then(res => {
        if (res?.getPeopleProfile?.peopleItem) {
          this.setIsIsSubscribedToSms(
            !!res.getPeopleProfile.peopleItem.isSubscribedToSms
          );
          this.setIsSubscribedToPromo(
            !!res.getPeopleProfile.peopleItem.isSubscribedToPromo
          );
          this.setIsSubscribedToEmail(
            !!res.getPeopleProfile.peopleItem.isSubscribedToEmail
          );
        }
      })
      .catch(error => {
        console.log('error:', error);
      });
  };

  getFullUserInfo = async (
    headerToken?: string,
    cartTokenNew?: string
  ): Promise<MeQuery['me'] | undefined | null> => {
    if (cartTokenNew) {
      localStorage.setItem('cartToken', JSON.stringify(cartTokenNew));
    }
    if (headerToken) {
      localStorage.setItem('headerToken', JSON.stringify(headerToken));
    }
    return await APIold.getCartToken(headerToken)
      .then(res => {
        const newUser = res.me ? { ...res.me, headerToken } : null;
        this.setUser(newUser);
        this.setIsUserLoading(false);
        return res.me;
      })
      .then(res => {
        APIStage.deliveryAddresses({
          cartToken: cartTokenNew || cartToken
        }).then(res => {
          if (res.deliveryAddresses) {
            this.setAddressesLastPickup(res.deliveryAddresses);
          }
        });
        return res;
      })
      .then(res => {
        this.updateIsload(true);
        return res;
      })
      .catch(error => {
        console.log(error);
        throw new Error(`Error: ${error}`);
      });
  };

  setAddressesLastPickup = (addresses: any): void => {
    this.addressesLastPickup = addresses || [];
  };

  setIsUserLoading = (bool: boolean): void => {
    this.isUserLoading = bool;
  };

  addPeopleAddress = async (
    data: PeopleAddressInput
  ): Promise<AddPeopleAddressMutation> =>
    await APIold.addPeopleAddress({ data }).then(async res => {
      await this.getFullUserInfo();
      return res;
    });

  updatePeopleAddress = async (
    data: PeopleAddressInput
  ): Promise<UpdatePeopleAddressMutation> =>
    await APIold.updatePeopleAddress({ data }).then(async res => {
      await this.getFullUserInfo();
      return res;
    });

  removeAddress = async (
    id: number
  ): Promise<MeQuery['me'] | null | undefined> =>
    await APIold.removePeopleAddress({ id })
      .then(async () => await this.getFullUserInfo())
      .catch(error => {
        console.log('error:', error);
        throw new Error(`Error: ${error}`);
      });

  // ИСПРАВИТЬ КОГДА НИБУДЬ
  setUser = (user: storeUserT | null): void => {
    // @ts-expect-error вместо {} нужно будет сделать null
    this.user = user !== null ? user : {};
  };

  setSettings = (settingsData: settingsT): void => {
    const settings = settingsData ? { ...settingsData } : defaultSettings;
    this.settings = {
      epp_catalog: settings.epp_catalog || EPP_CATALOG_DEFAULT,
      catalog_sorting: changeSortName(settings.catalog_sorting)
    };
  };

  updateIsload = (isLoad: boolean): void => {
    this.isLoad = isLoad;
  };

  logout = (): void => {
    const oneSignalLocalStorage = {
      isPushNotificationsEnabled:
        localStorage.getItem('isPushNotificationsEnabled') || undefined,
      isOptedOut: localStorage.getItem('isOptedOut') || undefined,
      'onesignal-notification-prompt':
        localStorage.getItem('onesignal-notification-prompt') || undefined
    };
    const cookieWarningShown = localStorage.getItem('cookieWarningShown');
    const cookieWarningDismissed = localStorage.getItem(
      'cookieWarningDismissed'
    );
    localStorage.clear();
    __deleteCookie('cAuth_People_id', '/', '*');

    API.logout()
      .then(() => {
        this.setUser(null);
        localStorage.setItem('cartToken', JSON.stringify(v1()));
        if (oneSignalLocalStorage.isPushNotificationsEnabled) {
          localStorage.setItem(
            'isPushNotificationsEnabled',
            oneSignalLocalStorage.isPushNotificationsEnabled
          );
        }
        if (oneSignalLocalStorage.isOptedOut) {
          localStorage.setItem('isOptedOut', oneSignalLocalStorage.isOptedOut);
        }
        if (oneSignalLocalStorage['onesignal-notification-prompt']) {
          localStorage.setItem(
            'onesignal-notification-prompt',
            oneSignalLocalStorage['onesignal-notification-prompt']
          );
        }
        if (cookieWarningShown) {
          localStorage.setItem('cookieWarningShown', cookieWarningShown);
        }
        if (cookieWarningDismissed) {
          localStorage.setItem(
            'cookieWarningDismissed',
            cookieWarningDismissed
          );
        }
      })
      .then(async () => {
        const regionDataStoreId = CartStore.regionData?.default_store_id;
        XStore.setXStoreId(regionDataStoreId ?? MOSCOW_DEFAULT_STORE_ID);
        if (isNewCart()) {
          return await APIold.clearCookie();
        }
        await Promise.resolve();
      })
      .then(() => {
        location.reload();
      })
      .catch(console.log);
  };

  getLoyaltyStatusUser = (): void => {
    queryClient
      .ensureQueryData(peopleBillAmountQueryOptions())
      .then(data => {
        const status = data?.peopleItem?.status_id;
        this.setLoyaltyStatusUser(Number(status));
      })
      .catch(console.log);
  };

  setLoyaltyStatusUser = (status: number): void => {
    this.loyaltyStatusUser = status || 1;
  };
}

const userStore = new Store();
export type UserStoreType = typeof userStore;
export default userStore;
